/***********************************************************************
 An instance of this class handles multiple sco's and act to the LMS
 as a single SCO with the given Scorm 1.2 API.
 The stored data will be saved compresses and encrypted in the
 suspend data field on LMS.
 ***********************************************************************/
import {initializeMasteryScore} from "course/initializeMasteryScore";
import {saveScoToPublisher} from "publisher/communication/saveScoToPublisher";
import {batchRequestToPublisher} from "publisher/communication/batchRequestToPublisher";
import {requestLearningScoDataFromPublisher} from "publisher/communication/requestLearningScoDataFromPublisher";

import {CourseToPublisherScormApi} from "publisher/communication/CourseToPublisherScormApi";
import {SingleScoData} from "scorm/scorm_1_2/SingleScoData";
import {SCORE_MODEL_ID,LESSON_LOCATION_MODEL_ID, LESSON_STATUS_MODEL_ID, ScoData} from "scorm/scorm_1_2/ScoData";
import {Sco} from "scorm/common/Sco";
import {ScormApiBase} from "scorm/common/ScormApiBase";
import {ScoRegistry} from "scorm/common/ScoRegistry";
import {SequenceDataMap} from "course/SequenceDataMap";
import {Event} from "common/Event";
import {CourseEvents} from "course/CourseEvents";
import {LoggingService} from "logging/LoggingService";
import {Assert} from "common/Assert";


export function PublisherCourse(serverUrl, courseId, language, defaultMasteryScore = 80 ){

    const courseScoId = `${courseId}_${language}`,
        publisherScormApi = new CourseToPublisherScormApi( serverUrl, courseScoId, true ),
        base = new ScormApiBase( initializeMasteryScore( defaultMasteryScore ) ),
        courseScoData = new SingleScoData(),
        scoRegistry = new ScoRegistry( ScoData ),
        domStructure = new SequenceDataMap();

    let activeSco = null,
        username = "";

    function initializeLearningScoDataFromPublisher() {
        requestLearningScoDataFromPublisher( serverUrl, domStructure.getResultMap() )
            .then( ( data ) => onDataUpdatedFromPublisher( data ) )
            .catch( (reason) => LoggingService.error( reason ) );
    }

    function onDataUpdatedFromPublisher( learningScoResultMap ){

        for (const [scoId, data] of learningScoResultMap.entries()) {

            let sco = scoRegistry.get( scoId );
            if( !sco ){
                sco = scoRegistry.initializeNewSco( scoId );
            }

            sco.setValue( SCORE_MODEL_ID, data.score );
            sco.setValue( LESSON_STATUS_MODEL_ID, data.lessonStatus );
            sco.setValue( LESSON_LOCATION_MODEL_ID, data.lessonLocation );
        }

        refreshUI();
    }

    function refreshUI() {
        // tell customizing to update DOM (update UI)
        Event.trigger(document, CourseEvents.BUFFER_REFRESHED);
    }

    function initializePublisherSession(){
        return publisherScormApi.LMSInitialize() === "true";
    }

    function terminatePublisherSession(){
        return publisherScormApi.LMSFinish() === "true";
    }

    function refreshCourseStatus() {
        courseScoData.refreshStatus( scoRegistry.getStatus(), scoRegistry.collectSuspendData(), base.getLapsedTime() );
    }

    function commit() {

        refreshCourseStatus();

        if( !courseScoData.checkSuspendDataSize() ) {
            LoggingService.warning( "You are connected to a scorm 1.2 LMS and the suspend_data is longer than 4096 bytes!" );
        }

        const lmsQueries = [];
        lmsQueries.push( `${serverUrl}/initialize/${courseScoId}` );

        const suspendDataToCommit = courseScoData.get("cmi.suspend_data").toString();
        lmsQueries.push( `${serverUrl}/set-value?key=cmi.suspend_data&value=${encodeURIComponent(suspendDataToCommit)}` );

        courseScoData.forEachLmsRelevantValue( (value, dataModelId) => {
            const stringValue = value ? value.toString() : "";
            lmsQueries.push( `${serverUrl}/set-value?key=${dataModelId}&value=${encodeURIComponent(stringValue)}` );
        });

        lmsQueries.push( `${serverUrl}/terminate` );

        batchRequestToPublisher( lmsQueries )
            .then( () => refreshUI() )
            .catch( reason => {
                LoggingService.error(reason);
            });

        return "true";
    }

    function terminateActiveLearningSco() {

        if( !activeSco ) {
            return;
        }

        const currentSCO = activeSco;

        // activeSco hast to be reset before saveScoToPublisher is called, so that Course-Navigation-Trigger do work,
        // else it could happen that you get the "There is a document still in use"-Message.
        activeSco = undefined;

        saveScoToPublisher( serverUrl, currentSCO )
            .then( () => {
                const changedData = currentSCO.getChangedData();
                for (const [key, value] of changedData.entries()) {
                    scoRegistry.setValue( currentSCO.getId(), key, value );
                }

                Event.trigger(document, CourseEvents.SEQUENCE_TERMINATED, {scoId: currentSCO.getId()});

                commit();
            });
    }

    // revealing public api
    return {

        domStructure : domStructure,
        username: username,

        /***********************************************************************
         * initializes the course by getting initial values from lms.
         * @return {String} true / false
         ***********************************************************************/
        initialize: () => {

            if( !initializePublisherSession() ) {
                return "false";
            }

            // read only lms values -------------------------------------------------------
            courseScoData.set( "cmi.student_data.mastery_score", defaultMasteryScore );

            const coreChildrenCSV = publisherScormApi.LMSGetValue("cmi.core._children");
            if (coreChildrenCSV.indexOf("credit") !== -1) {
                courseScoData.set( "cmi.core.credit", publisherScormApi.LMSGetValue("cmi.core.credit") );
            }

            // read and write --------------------------------------------------
            if (coreChildrenCSV.indexOf("lesson_location") !== -1) {
                courseScoData.set( "cmi.core.lesson_location", publisherScormApi.LMSGetValue("cmi.core.lesson_location") );
            }

            if (coreChildrenCSV.indexOf("lesson_status") !== -1) {
                courseScoData.set( "cmi.core.lesson_status", publisherScormApi.LMSGetValue("cmi.core.lesson_status") );
                if( courseScoData.isNotAttempted() ) {
                    courseScoData.set( "cmi.core.lesson_status", "incomplete" );
                    publisherScormApi.LMSSetValue("cmi.core.lesson_status", "incomplete" );
                }
            }

            if (coreChildrenCSV.indexOf("score") !== -1) {
                const scoreChildrenCSV = publisherScormApi.LMSGetValue("cmi.core.score._children");
                if (scoreChildrenCSV.indexOf("raw") !== -1) {
                    courseScoData.set( "cmi.core.score.raw", publisherScormApi.LMSGetValue("cmi.core.score.raw") );
                }
            }
            const encryptedSuspendData = publisherScormApi.LMSGetValue("cmi.suspend_data");
            courseScoData.set( "cmi.suspend_data", encryptedSuspendData );
            scoRegistry.initialize( encryptedSuspendData );

            const courseModelIds = [
                "cmi.student_data.max_time_allowed",
                "cmi.student_data.time_limit_action",
                "cmi.core.student_name"
            ];

            courseModelIds.forEach( ( dataModelId) => {
                courseScoData.set( dataModelId, publisherScormApi.LMSGetValue(dataModelId) );
            });

            if( username === "" ) {
                username = courseScoData.get("cmi.core.student_name");
            }

            return terminatePublisherSession() ? "true" : "false";
        },

        /***********************************************************************
         * gets the course mastery score
         * @return {Number} mastery score
         ***********************************************************************/
        getMasteryScore: () => Number( courseScoData.get("cmi.student_data.mastery_score") ),

        /***********************************************************************
         * gets the completion threshold. What is the mastery score for scorm 1.2.
         * @return {Number} mastery score
         ***********************************************************************/
        getCompletionThreshold: () => Number( courseScoData.get("cmi.student_data.mastery_score") ),

        /***********************************************************************
         * Calculates the current course score and status and commit all updated
         * values to the lms.
         * @return {String} true / false
         ***********************************************************************/
        commit: commit,

        /***********************************************************************
         * sets the list for the scoring relevant scos
         * @param {boolean} useAssessments
         * @return {String} true
         ***********************************************************************/
        setScoreList: (useAssessments) => {
            domStructure.initScoreIDs(useAssessments);
            scoRegistry.setRelevantScoList( domStructure.getScoreIDs() );
            return "true";
        },

        /***********************************************************************
         * gets a value for a learning sco or the course sco
         * @param {String} scoId the id of the sco. can be null to get a value from the lms
         * @param {String} dataModelId scorm data model id for the the value
         * @return {String} value or an empty string, if the dataModelId is not defined
         ***********************************************************************/
        getValueFromCourseOrLearningSco: (scoId, dataModelId) => {

            if( scoId === null ) {
                return courseScoData.get( dataModelId );
            }

            if( scoRegistry.contains( scoId ) ) {
                return scoRegistry.getValue( scoId, dataModelId );
            }

            return "";
        },

        /***********************************************************************
         * gets a value for the course
         * @param {String} dataModelId scorm data model id for the the value
         * @return {String} value or an empty string, if the does not exist
         ***********************************************************************/
        getValue : ( dataModelId ) => {

            Assert.checkArgument( dataModelId );

            if( !courseScoData.isValid( dataModelId ) ) {
                return undefined;
            }

            return courseScoData.get( dataModelId );
        },

        /***********************************************************************
         * gets a value for the course
         * @param {String} dataModelId scorm data model id for the the value
         * @param {String} value value
         * @return {String} true / false
         ***********************************************************************/
        setValue : ( dataModelId, value ) => {

            Assert.checkArgument( dataModelId );

            if( !courseScoData.isValid( dataModelId ) ) {
                return "false";
            }

            return courseScoData.set( dataModelId, value ) ? "true" : "false";
        },

        /***********************************************************************
         * gets a value for a learning sco
         * @param {String} scoId the id of the sco. can be null to get a value from the lms
         * @param {String} dataModelId scorm data model id for the the value
         * @return {String} value or undefined
         ***********************************************************************/
        getLearningScoValue: (scoId, dataModelId) => {

            if( scoRegistry.contains( scoId ) ) {
                return scoRegistry.getValue( scoId, dataModelId );
            }

            return undefined;
        },

        terminate: function() {

            if( activeSco ){
                terminateActiveLearningSco();
            }

            return "true";
        },

        initializeActiveLearningSco: ( scoId ) => {

            if( !scoId || activeSco ){
                return false;
            }

            if( !scoRegistry.contains(scoId) ) {
                scoRegistry.initializeNewSco( scoId );
                scoRegistry.setValue( scoId, "lesson_id", scoId );
            }

            const scoData = scoRegistry.get( scoId );
            activeSco = new Sco( scoId, scoData );

            return true;
        },
        hasActiveLearningSco: () => !!activeSco,
        getActiveLearningSco: () => activeSco,
        terminateActiveLearningSco: terminateActiveLearningSco,
        onDomAndModelSynchronized: initializeLearningScoDataFromPublisher
    };
}
